/**
 * MyWorkspaces — Customer workspace management (DOKS K8s)
 */
import { useEffect, useCallback } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api, ApiError } from '../api/client';
import { user } from '../stores/auth.store';
import { SkeletonBar, Button, Input, Select, Modal } from '../components/ui';

import { T } from '../styles/tokens';
interface Workspace {
  id: string;
  name: string;
  image: string;
  plan: string;
  workspace_url: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface WorkspaceStatus {
  phase: string;
  replicas: number;
  readyReplicas: number;
  pods: Array<{ name: string; phase: string; nodeName: string; startTime: string }>;
}

const workspaces = signal<Workspace[]>([]);
const loading = signal(true);
const error = signal('');
const showCreate = signal(false);
const createLoading = signal(false);
const logsModal = signal<{ id: string; logs: string } | null>(null);
const statusModal = signal<{ id: string; status: WorkspaceStatus } | null>(null);

// Form
const formName = signal('');
const formImage = signal('nginx:latest');
const formPlan = signal('starter');
const formPort = signal(8080);

async function fetchWorkspaces() {
  try {
    const data = await api.get<{ workspaces: Workspace[] }>('/cloud/workspaces/my');
    workspaces.value = data.workspaces;
    error.value = '';
  } catch (e: any) {
    error.value = e.message || 'Erro ao carregar workspaces';
  } finally {
    loading.value = false;
  }
}

async function createWorkspace() {
  createLoading.value = true;
  try {
    await api.post('/cloud/workspaces', {
      name: formName.value,
      image: formImage.value,
      plan: formPlan.value,
      port: formPort.value,
    });
    showCreate.value = false;
    formName.value = '';
    await fetchWorkspaces();
  } catch (e: any) {
    alert(e.message || 'Erro ao criar workspace');
  } finally {
    createLoading.value = false;
  }
}

async function deleteWorkspace(id: string) {
  if (!confirm('Deletar workspace? Esta ação é irreversível.')) return;
  try {
    await api.delete(`/cloud/workspaces/${id}`);
    await fetchWorkspaces();
  } catch (e: any) {
    alert(e.message || 'Erro ao deletar');
  }
}

async function restartWorkspace(id: string) {
  try {
    await api.post(`/cloud/workspaces/${id}/restart`);
    alert('Workspace reiniciando...');
    setTimeout(fetchWorkspaces, 3000);
  } catch (e: any) {
    alert(e.message || 'Erro ao reiniciar');
  }
}

async function viewLogs(id: string) {
  try {
    const data = await api.get<{ logs: string }>(`/cloud/workspaces/${id}/logs`);
    logsModal.value = { id, logs: data.logs };
  } catch (e: any) {
    alert(e.message || 'Erro ao buscar logs');
  }
}

async function viewStatus(id: string) {
  try {
    const data = await api.get<WorkspaceStatus>(`/cloud/workspaces/${id}/status`);
    statusModal.value = { id, status: data };
  } catch (e: any) {
    alert(e.message || 'Erro ao buscar status');
  }
}

const STATUS_COLORS: Record<string, string> = {
  running: T.success,
  creating: T.warning,
  pending: T.warning,
  restarting: T.primary,
  failed: T.danger,
  deleted: T.textSecondary,
};

export function MyWorkspacesPage() {
  useEffect(() => {
    fetchWorkspaces();
    const interval = setInterval(fetchWorkspaces, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading.value) return (<div style={{ display: 'grid', gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
              <SkeletonBar width="60%" height={16} />
              <div style={{ height: 8 }} />
              <SkeletonBar width="80%" height={12} />
              <div style={{ height: 6 }} />
              <SkeletonBar width="40%" height={12} />
            </div>
          ))}
        </div>);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text }}>Workspaces</h1>
        <Button variant="primary" onClick={() => { showCreate.value = true; }}>
          + Novo Workspace
        </Button>
      </div>

      {error.value && (
        <div style={{ padding: 12, background: T.dangerBg, borderRadius: 8, color: T.danger, marginBottom: 16 }}>
          {error.value}
        </div>
      )}

      {workspaces.value.length === 0 && !loading.value && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: T.textSecondary }}>
          <p style={{ fontSize: 48, marginBottom: 16 }}></p>
          <p style={{ fontSize: 18, marginBottom: 8 }}>Nenhum workspace ainda</p>
          <p>Crie seu primeiro workspace para começar</p>
        </div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {workspaces.value.map(ws => (
          <div key={ws.id} style={{ background: T.bg, borderRadius: 12, padding: 20, border: '1px solid #2c2c2e' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: T.text, marginBottom: 4 }}>{ws.name}</h3>
                <span style={{ fontSize: 12, color: T.textSecondary }}>{ws.image} • {ws.plan}</span>
              </div>
              <span style={{
                padding: '4px 12px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                color: T.text,
                background: STATUS_COLORS[ws.status] || T.textSecondary,
              }}>
                {ws.status}
              </span>
            </div>

            {ws.workspace_url && (
              <a href={ws.workspace_url} target="_blank" rel="noopener" style={{ color: T.primary, fontSize: 14, display: 'block', marginBottom: 12 }}>
                {ws.workspace_url}
              </a>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="ghost" size="sm" onClick={() => viewStatus(ws.id)}>Status</Button>
              <Button variant="ghost" size="sm" onClick={() => viewLogs(ws.id)}>Logs</Button>
              <Button variant="primary" size="sm" onClick={() => restartWorkspace(ws.id)}>Restart</Button>
              <a href={`/k8s-terminal/${ws.id}`} style={{ ...btnStyle(T.accent), textDecoration: 'none' }}>Terminal</a>
              {ws.workspace_url && (
                <a href={ws.workspace_url} target="_blank" rel="noopener" style={{ ...btnStyle(T.success), textDecoration: 'none' }}>Abrir</a>
              )}
              <Button variant="danger" size="sm" onClick={() => deleteWorkspace(ws.id)}>Deletar</Button>
            </div>

            <div style={{ marginTop: 8, fontSize: 11, color: '#636366' }}>
              ID: {ws.id} • Criado: {new Date(ws.created_at).toLocaleString('pt-BR')}
            </div>
          </div>
        ))}
      </div>

      {/* Create Modal */}
      <Modal open={showCreate.value} onClose={() => { showCreate.value = false; }} title="Novo Workspace">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Nome" value={formName.value} onChange={(v) => { formName.value = v; }} placeholder="meu-app" />
          <Input label="Imagem Docker" value={formImage.value} onChange={(v) => { formImage.value = v; }} placeholder="nginx:latest" />
          <Select
            label="Plano"
            value={formPlan.value}
            onChange={(v) => { formPlan.value = v; }}
            options={[
              { value: 'starter', label: 'Starter (512MB, 1 réplica)' },
              { value: 'pro', label: 'Pro (1GB, até 4 réplicas)' },
              { value: 'business', label: 'Business (2GB, até 8 réplicas)' },
            ]}
          />
          <Input label="Porta" type="number" value={formPort.value} onChange={(v) => { formPort.value = parseInt(v) || 0; }} placeholder="8080" />
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <Button variant="ghost" onClick={() => { showCreate.value = false; }}>Cancelar</Button>
            <Button variant="primary" onClick={createWorkspace} disabled={createLoading.value || !formName.value} loading={createLoading.value}>
              {createLoading.value ? 'Criando...' : 'Criar Workspace'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Logs Modal */}
      <Modal open={!!logsModal.value} onClose={() => { logsModal.value = null; }} title={`Logs — ${logsModal.value?.id || ''}`} size="lg">
        <pre style={{ background: T.bg, color: '#0f0', padding: 16, borderRadius: 8, maxHeight: 400, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap' }}>
          {logsModal.value?.logs || 'Sem logs disponíveis'}
        </pre>
      </Modal>

      {/* Status Modal */}
      <Modal open={!!statusModal.value} onClose={() => { statusModal.value = null; }} title={`Status — ${statusModal.value?.id || ''}`}>
        {statusModal.value && (
          <>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: T.textSecondary }}>Phase: </span>
              <span style={{ color: STATUS_COLORS[statusModal.value.status.phase.toLowerCase()] || '#fff', fontWeight: 600 }}>{statusModal.value.status.phase}</span>
              <span style={{ color: T.textSecondary, marginLeft: 16 }}>Replicas: {statusModal.value.status.readyReplicas}/{statusModal.value.status.replicas}</span>
            </div>
            {statusModal.value.status.pods.map(pod => (
              <div key={pod.name} style={{ padding: 8, background: T.bgModal, borderRadius: 6, marginBottom: 6, fontSize: 13 }}>
                <div style={{ color: T.text }}>{pod.name}</div>
                <div style={{ color: T.textSecondary }}>Phase: {pod.phase} • Node: {pod.nodeName} • Start: {pod.startTime ? new Date(pod.startTime).toLocaleString('pt-BR') : 'N/A'}</div>
              </div>
            ))}
          </>
        )}
      </Modal>
    </div>
  );
}

function btnStyle(bg: string): any {
  return { padding: '6px 14px', background: bg, color: T.text, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 };
}

const overlayStyle: any = { position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle: any = { background: T.bg, borderRadius: 16, padding: 24, width: '90%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto' };
const labelStyle: any = { display: 'block', fontSize: 13, color: T.textSecondary, marginBottom: 4, marginTop: 12 };
const inputStyle: any = { width: '100%', padding: '10px 12px', background: T.bgModal, border: '1px solid #3a3a3c', borderRadius: 8, color: T.text, fontSize: 14, boxSizing: 'border-box' };
